import { queryClient } from "@/App/Providers";
import { AppsmenError, Jwt } from "@/shared/utils/interfaces";
import axios, { AxiosRequestConfig } from "axios";
import { reportAccessDenied } from "./atoms/useAccessDenied";
import { generalToastId } from "./utils/constant";
import { errorToast } from "./utils/helpers";

declare module "axios" {
  interface AxiosRequestConfig {
    // The caller handles a 403 itself, so no access denied modal
    ignoreAccessDenied?: boolean;
  }
}

export const axiosClient2 = axios.create({
  baseURL: import.meta.env.VITE_API_SERVER,
  timeout: 45000,
});
let timeOut = null;

const signOut = () => {
  localStorage.removeItem("jwt");
  localStorage.removeItem("branchAtom");
  localStorage.removeItem("franchiseAtom");
  localStorage.removeItem("merchantAtom");
  localStorage.removeItem("authority");
  queryClient.clear();
};

const refreshToken = async () => {
  const local = localStorage.getItem("jwt");
  const jwt: Jwt = JSON.parse(local);

  if (jwt?.refresh_token !== undefined) {
    axios
      .post(`${import.meta.env.VITE_API_SERVER}/token/refresh`, {
        refresh_token: jwt?.refresh_token,
      })
      .then(({ data: { token } }) => {
        localStorage.setItem("jwt", JSON.stringify({ ...jwt, token }));
      })
      .catch((error) => {
        if (import.meta.env.PROD || error.response.status === 401) {
          signOut();
          window.location.href = "/login?error=401";
        }
      });
  }
};

axiosClient2.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  async (error: AppsmenError) => {
    timeOut && clearTimeout(timeOut);

    if (error.response?.status === 400) {
      timeOut = setTimeout(() => {
        if (error.response?.data?.error_id === "assertError") {
          errorToast(error.response?.data?.error_message, generalToastId);
        } else {
          errorToast("errors.400", generalToastId);
        }
      }, 500);
    } else if (error.response?.status === 401) {
      refreshToken();
    } else if (error.response?.status === 403) {
      // The role has no access, the user stays signed in and gets a modal explaining it
      if (!error.config?.ignoreAccessDenied) {
        reportAccessDenied({
          kind: "request",
          page: window.location.pathname,
          method: error.config?.method?.toUpperCase(),
          url: error.config?.url,
          params: error.config?.params,
          status: error.response.status,
          errorId: error.response.data?.error_id,
          errorMessage: error.response.data?.error_message,
        });
      }
    } else if (
      error.response?.status === 406 &&
      // handled by the menu data tags delete confirmation
      error.response?.data?.error_id !== "menuDataTagInUse"
    ) {
      timeOut = setTimeout(() => {
        errorToast(error.response?.data?.error_message, generalToastId);
      }, 500);
    }

    return Promise.reject(error);
  }
);

axiosClient2.interceptors.request.use(function (config) {
  const token = JSON.parse(localStorage.getItem("jwt"))?.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const language = localStorage.getItem("language");
  if (language) {
    config.headers.locale = language;
  }
  const country = localStorage.getItem("country");
  if (country) {
    config.headers.country = country;
  }

  return config;
});

export const get2 = async (url: string, config?: AxiosRequestConfig) => {
  const { data } = await axiosClient2.get(url, config);
  return data;
};

export const post2 = async (
  url: string,
  body?: any,
  config?: AxiosRequestConfig
) => {
  const { data } = await axiosClient2.post(url, body, config);
  return data;
};

export const put2 = async (
  url: string,
  body: any,
  config?: AxiosRequestConfig
) => {
  const { data } = await axiosClient2.put(url, body, config);
  return data;
};

export const patch = async (
  url: string,
  body?: any,
  config?: AxiosRequestConfig
) => {
  const { data } = await axiosClient2.patch(url, body, config);
  return data;
};

export const destroy2 = async (url: string, config?: AxiosRequestConfig) => {
  const { data } = await axiosClient2.delete(url, config);
  return data;
};
